<template>
  <lendi-navigation-tile
      :title="title"
      :action="action"
      :to="to"
      :data-test="navigationLinkId"
      :icon="routeObject.meta.icon || ''"
      :badgeText="badgeText"
      :childCondition="(child) => child.meta"
      @child-click="goToChildView($event)"
      :children="children"
      :isSameRouteAsChildCondition="isSameRouteAsChild"
      :class="tourStepClass">
    <template #child="{child}">
      <template v-if="child.meta">
        <v-list-item-icon v-if="child.meta.icon">
          <v-badge
              class="discount-badge"
              :color="child.meta.badge?.color || 'error'"
              overlap
              :value="!!child.meta.badge?.is && !!child.meta.badge?.text"
              :content="child.meta.badge?.text">
            <v-icon
              :class="{'white--text': isSameRouteAsChild(child)}"
              class="pt-1">
              {{ child.meta.icon }}
            </v-icon>
          </v-badge>
        </v-list-item-icon>
        <v-list-item-title :class="{'white--text': isSameRouteAsChild(child)}">{{ child.meta.title }}</v-list-item-title>
      </template>
    </template>
      <template #title>
        <v-list-item-action v-if="routeObject.meta && (routeObject.meta.icon || routeObject.meta.customIcon)">
          <v-badge
              class="discount-badge"
              :color="badgeColor"
              overlap
              :value="showBadge"
              :content="badgeText">
            <asystent-360-mesh-orb v-if="routeObject.meta.customIcon === 'asystent360'" />
            <v-icon v-else>{{ routeObject.meta.icon }}</v-icon>
          </v-badge>
        </v-list-item-action>
        <v-list-item-content>
          <v-list-item-title>{{ title }}</v-list-item-title>
        </v-list-item-content>
      </template>
  </lendi-navigation-tile>
</template>
<script lang="ts">
import Vue from 'vue';
import {Component, Prop} from 'vue-property-decorator';
import { RouteConfig} from 'vue-router';
import LendiNavigationTile from '@/commons/LendiNavigationTile.vue';
import Asystent360MeshOrb from '@/commons/Asystent360MeshOrb.vue';
import {BikKey} from '@/modules/onboarding/tours/enums/BikKey';
import {AutomationTestsIds} from '@/env/AutomationTests/ids/AutomationTestsIds';
@Component({
  components: {LendiNavigationTile, Asystent360MeshOrb,},
})
export default class ListMenu extends Vue {
  @Prop()
  readonly routeObject!: RouteConfig;

  public get navigationLinkId(): string {
    return AutomationTestsIds.navigationLink(this.routeObject.path);
  }

  public get tourStepClass() {
    const key = this.routeObject.meta?.key;
    if (!key) return '';
    
    // Check if the key is from BikKey enum
    if (Object.values(BikKey).includes(key)) {
      return this.$onboarding.tours.BIK.step[key];
    }
    
    // Default to DASHBOARD tour
    return this.$onboarding.tours.DASHBOARD.step[key];
  }

  /**
   * `badge.condition` pozwala pokazać znacznik tylko części użytkowników — np. „New" wyłącznie
   * tym, którzy realnie zobaczą nową funkcję. Brak warunku = badge widoczny jak dotąd.
   */
  private get isBadgeVisible(): boolean {
    const badge = this.routeObject.meta?.badge;
    return !!badge && !badge.is?.hidden && (badge.condition ? badge.condition() : true);
  }

  public get showBadge() {
    return this.isBadgeVisible;
  }

  public get title() {
    const title = this.routeObject.meta.title;
    return typeof title === 'function' ? title() : title;
  }

  public get badgeText() {
    return this.isBadgeVisible ? this.routeObject.meta?.badge?.text || '' : '';
  }

  public get badgeColor() {
    return this.isBadgeVisible ? this.routeObject.meta?.badge?.color || 'error' : '';
  }

  public async goToChildView(childRoutes: RouteConfig) {
    if (typeof childRoutes.meta?.action === 'function') {
      try {
        // action() may be sync or async (e.g. openSmeComparatorModal lazy-loads a chunk) —
        // await catches both a synchronous throw and a rejected Promise (e.g. chunk load failure).
        await childRoutes.meta.action();
      } catch (e) {
        this.errorHandler(e);
      }
    } else if (childRoutes.name) {
      this.$router.push({name: childRoutes.name, });
    } else if (childRoutes.path) {
      window.location.href = childRoutes.path;
    }
  }

  get children() {
    return this.routeObject.children?.filter(x => x.meta) || [];
  }

  get action() {
    return this.routeObject.meta && typeof this.routeObject.meta.action === 'function'
      ? this.routeObject.meta.action
      : undefined;
  }

  get to() {
    return this.routeObject.meta && this.routeObject.meta.action ? undefined : `/app/${this.routeObject.path}`;
  }

  public isSameRouteAsChild(child: RouteConfig) {
    if (child?.name?.startsWith('productType') && (child?.name?.endsWith('-redirect') || child?.name?.endsWith('-form'))) {
      const childMatched = child.name.match(/productType[a-zA-Z]+-/);
      const routeMatched = this.$route.name?.match(/productType[a-zA-Z]+-/);
      return childMatched && routeMatched && childMatched[0] === routeMatched[0];
    } else return child.name === this.$route.name;
  }
}
</script>
